import { tt } from '@agos/shared'

/**
 * Whether the restaurant is taking orders — FB-C06, and the most consequential finding of the C0 pass.
 *
 * **Legacy's check fails open.** `checkStoreOpen` gates every add-to-cart on the homepage and begins:
 *
 * ```ts
 * if (!data) return true; // no config saved = assume open
 * ```
 *
 * So a failed or slow `GET /settings/opening-hours` means *open*, and the customer fills a basket and
 * places an order.
 *
 * **And nothing behind it catches that.** `availability.requireOpenToPublic()` checks exactly two
 * things — maintenance mode and the online-orders switch — and **not the hours**. Those are compared in
 * one place only:
 *
 * ```java
 * if (fromTheWeb && request.scheduledAt() != null) {
 *   availability.requireWithinOpeningHours(request.scheduledAt());
 * }
 * ```
 *
 * An immediate order carries no `scheduledAt`, so it is never compared against anything. Measured on the
 * restored data: `maintenance_mode = false`, `online_orders_enabled = true`, hours 09:45–22:00 with a
 * 21:47 cutoff on six days and 12:00–22:00 on Sunday. An order placed at 03:00 passes both switches and
 * is written. The one mitigation is `auto_accept = false`, so it waits in `pending` — a queue somebody
 * reads in the morning, not a refusal.
 *
 * So here, **unknown is its own state**. The menu still browses on it, because browsing does not depend
 * on the answer; ordering is not offered under a claim this code cannot make. The server half is filed.
 *
 * The cutoff arithmetic itself is ported exactly, including the overnight case, which is correct in
 * legacy and is the kind of thing rewritten wrong.
 */

export interface OpeningDay {
  open: boolean
  from: string
  to: string
  /** When order-taking stops, which is not when the door closes. Blank means *at closing time*. */
  orderCutoff?: string
}

export interface OpeningHours {
  emergencyClosed?: boolean
  days?: Record<string, OpeningDay | undefined>
}

export type OpenState = 'open' | 'closed' | 'emergency' | 'unknown'

/**
 * The restaurant's own notice — §9.243, FB-403, the owner's N1.
 *
 * **A field that has been on the wire since the port and read by nobody.** The manager writes it,
 * `GET /settings/opening-hours` publishes it unauthenticated, `TenantManagerSettingsService` says in
 * its own docblock that *"the customer site reads the JSON one"* — and `openingHoursOf` declared
 * `{ emergencyClosed, days }` and dropped it. The owner could write a notice, save it, see the
 * manager's preview, and no customer would ever see it.
 *
 * **Its own reader rather than a field on {@link OpeningHours}.** The hours are re-read every minute
 * by `useMinute` because whether the shop is open changes with the clock; an announcement changes
 * when somebody edits it. Two readers over one payload keeps a change to either from reshaping the
 * other.
 */
export interface Announcement {
  message: string
  showAlways: boolean
}

export function announcementOf(payload: unknown): Announcement | null {
  if (typeof payload !== 'object' || payload === null) return null
  const raw = (payload as { announcement?: unknown }).announcement
  if (typeof raw !== 'object' || raw === null) return null
  const row = raw as { enabled?: unknown; message?: unknown; showAlways?: unknown }
  // Enabled and non-empty are legacy's first two conditions, and an enabled-but-empty announcement
  // is a real state — §9.157 warns about it on the manager side rather than preventing it.
  if (row.enabled !== true) return null
  const message = typeof row.message === 'string' ? row.message.trim() : ''
  if (message === '') return null
  return { message, showAlways: row.showAlways === true }
}

/**
 * Legacy's third condition — `Home.tsx:314`, `showAlways || !isOpen`.
 *
 * `showAlways` is what makes this more than a closed sign: a notice about a changed number or a
 * delivery delay has to be readable while the restaurant is taking orders. An **unknown** state
 * counts as not-open, so a failed hours read still shows the notice rather than hiding it — the
 * announcement is the operator's own words and suppressing them on a network error is the wrong way
 * round.
 */
export function announcementShows(
  announcement: Announcement | null,
  state: OpenState,
): announcement is Announcement {
  if (announcement === null) return false
  return announcement.showAlways || state !== 'open'
}


/**
 * The hours out of an unknown payload.
 *
 * Every reader of `/settings/footer` needs this, and reading it wrongly is how the whole week silently
 * becomes "unknown": the `days` bag is keyed by three-letter day, and a missing key is a legitimate
 * answer (a day the operator never configured), which is why nothing here fills one in.
 */
export function openingHoursOf(payload: unknown): OpeningHours | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as { emergencyClosed?: unknown; days?: unknown }
  const rawDays = typeof row.days === 'object' && row.days !== null ? (row.days as Record<string, unknown>) : {}
  const days: Record<string, OpeningDay> = {}
  for (const [key, value] of Object.entries(rawDays)) {
    if (typeof value !== 'object' || value === null) continue
    const day = value as Record<string, unknown>
    days[key] = {
      open: day.open === true,
      from: typeof day.from === 'string' ? day.from : '',
      to: typeof day.to === 'string' ? day.to : '',
      orderCutoff: typeof day.orderCutoff === 'string' ? day.orderCutoff : undefined,
    }
  }
  return { emergencyClosed: row.emergencyClosed === true, days }
}

/**
 * The week as the footer prints it: consecutive days with identical hours collapse into one row.
 *
 * *Pondelok – Sobota  09:45 – 22:00* rather than six identical lines. Legacy's `groupOpeningDays`,
 * ported with its ordering — Monday first, not Sunday, which is the week a Slovak restaurant runs on.
 *
 * A closed day never merges with the day beside it, even with another closed day: legacy lists each on
 * its own line, and a customer scanning for *when are you shut* wants the day named.
 */
export function weekRows(hours: OpeningHours | null): Array<{ dayKeys: string[]; hours: string | null }> {
  const days = hours?.days ?? {}
  const order = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
  const rows: Array<{ dayKeys: string[]; hours: string | null }> = []
  let index = 0
  while (index < order.length) {
    const key = order[index]!
    const day = days[key]
    if (day === undefined || !day.open) {
      rows.push({ dayKeys: [key], hours: null })
      index += 1
      continue
    }
    let end = index + 1
    while (end < order.length) {
      const next = days[order[end]!]
      if (next === undefined || !next.open || next.from !== day.from || next.to !== day.to) break
      end += 1
    }
    rows.push({ dayKeys: order.slice(index, end), hours: `${day.from} – ${day.to}` })
    index = end
  }
  return rows
}

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

function minutesOf(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (match === null) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

/** The cutoff if there is one, otherwise the closing time — legacy's rule, and the right one. */
export function cutoffOf(day: OpeningDay): string {
  const cutoff = (day.orderCutoff ?? '').trim()
  return cutoff === '' ? day.to : cutoff
}

/**
 * Whether an order may be placed now.
 *
 * `null` hours are **`unknown`**, never `open`. That single line is FB-C06.
 */
export function openState(hours: OpeningHours | null | undefined, now: Date): OpenState {
  if (hours === null || hours === undefined) return 'unknown'
  if (hours.emergencyClosed === true) return 'emergency'

  const today = hours.days?.[DAY_KEYS[now.getDay()] ?? '']
  if (today === undefined) return 'unknown'
  if (!today.open) return 'closed'

  const from = minutesOf(today.from)
  const to = minutesOf(cutoffOf(today))
  if (from === null || to === null) return 'unknown'

  const nowMinutes = now.getHours() * 60 + now.getMinutes()

  if (to <= from) {
    // Overnight, e.g. 22:00–02:00: open from `from` onwards, or before `to` on the *next* calendar day,
    // which means yesterday's row is the one that decides the small hours.
    if (nowMinutes >= from) return 'open'
    const yesterday = hours.days?.[DAY_KEYS[(now.getDay() + 6) % 7] ?? '']
    if (yesterday?.open === true) {
      const yesterdayTo = minutesOf(cutoffOf(yesterday))
      const yesterdayFrom = minutesOf(yesterday.from)
      if (yesterdayTo !== null && yesterdayFrom !== null && yesterdayTo <= yesterdayFrom) {
        return nowMinutes < yesterdayTo ? 'open' : 'closed'
      }
    }
    return 'closed'
  }

  return nowMinutes >= from && nowMinutes < to ? 'open' : 'closed'
}

export function mayOrder(state: OpenState): boolean {
  return state === 'open'
}

/**
 * What the screen says, per state.
 *
 * `unknown` says what is true — the hours could not be read — rather than either claiming the restaurant
 * is shut or, as legacy does, quietly assuming it is open.
 */
export function openNote(state: OpenState): string {
  if (state === 'open') return ''
  // Legacy's checkout, verbatim — `Checkout.tsx` has these two and no more. An earlier draft added a
  // second sentence to the first and rewrote the second entirely, which §8 does not allow (FB-C51).
  if (state === 'emergency') return tt('Reštaurácia je dočasne zatvorená.')
  if (state === 'closed') return tt('Reštaurácia je momentálne zatvorená.')
  // `unknown` is this port's own state — FB-C06. Legacy has no equivalent because it assumes open when
  // the hours fail to load and takes the order anyway, so there is no legacy wording to match.
  return tt(
    'Nepodarilo sa načítať otváracie hodiny, takže nevieme povedať, či máme otvorené. Objednávku zatiaľ nedáme - menu si pozrieť môžete.',
  )
}

/**
 * The same fact, as the **home banner** says it.
 *
 * Legacy words the two places differently — `Momentálne zatvorené` in the red bar across the menu,
 * `Reštaurácia je momentálne zatvorená.` on the checkout — and §8 is byte-for-byte, so one shared
 * sentence cannot serve both. The banner is a headline beside the opening time; the checkout line is a
 * sentence explaining why a button will not work.
 */
export function bannerNote(state: OpenState): string {
  if (state === 'open') return ''
  if (state === 'emergency') return tt('Dnes mimoriadne zatvorené')
  if (state === 'closed') return tt('Momentálne zatvorené')
  return tt('Otváracie hodiny sa nepodarilo načítať')
}

